<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import IconSelect from '../Icon/IconSelect.vue'
import ShowImage from '../Media/ShowImage.vue'
import { useLocationWefoValidation } from '../../composables/useLocationWefoValidation'

interface IconOption {
  id: number
  name: string
  image_url: string | null
}

interface SelectedIcon {
  id: number
  name: string
  image_url: string | null
  description: string
  sort_order: number
}

interface PriceRow {
  label: string
  price: string
}

interface WefoData {
  tile_title: string
  title1: string
  title2: string
  description: string
  location_text: string
  walking_time: number | null
  index_enabled: boolean
  index_titles: string[]
  free_admission: boolean
  prices: PriceRow[]
  price_level: string
}

const props = defineProps<{
  wefoEnabled?: boolean
  wefoData?: WefoData | null
  allIcons?: IconOption[]
  selectedIcons?: SelectedIcon[]
  locationId?: number | null
  errors?: Record<string, string[]>
}>()

const emit = defineEmits<{
  'update:wefoEnabled': [value: boolean]
  'update:wefoData': [value: WefoData]
  'update:wefoIcons': [value: SelectedIcon[]]
}>()

const defaults: WefoData = {
  tile_title: '',
  title1: '',
  title2: '',
  description: '',
  location_text: '',
  walking_time: null,
  index_enabled: false,
  index_titles: [],
  free_admission: false,
  prices: [],
  price_level: '',
}

const enabled = ref(props.wefoEnabled ?? false)
const data = ref<WefoData>({ ...defaults })

// Validation
const { v$ } = useLocationWefoValidation(data, enabled)
defineExpose({ v$ })

onMounted(() => {
  if (props.wefoData) {
    data.value = { ...defaults, ...props.wefoData }
  }
})

watch(enabled, (val) => {
  emit('update:wefoEnabled', val)
}, { immediate: true })

watch(data, (val) => {
  emit('update:wefoData', val)
}, { deep: true })

const onIconsChanged = (icons: SelectedIcon[]) => {
  emit('update:wefoIcons', icons)
}

// Index titles repeater
const addIndexTitle = () => {
  data.value.index_titles.push('')
}
const removeIndexTitle = (index: number) => {
  data.value.index_titles.splice(index, 1)
}

// Prices repeater
const addPrice = () => {
  data.value.prices.push({ label: '', price: '' })
}
const removePrice = (index: number) => {
  data.value.prices.splice(index, 1)
}

const getError = (field: string): string | null => {
  // Server errors take priority
  if (props.errors && props.errors[field]) {
    return props.errors[field][0]
  }
  // Then vuelidate errors
  const vField = (v$.value as any)[field]
  if (vField?.$errors?.length) {
    return vField.$errors[0].$message
  }
  return null
}

const hasError = (field: string): boolean => {
  return !!getError(field)
}

const getPriceError = (index: number, field: 'label' | 'price'): string | null => {
  // Server errors
  const serverKey = `wefo_data.prices.${index}.${field}`
  if (props.errors && props.errors[serverKey]) {
    return props.errors[serverKey][0]
  }
  // Vuelidate
  const pricesField = (v$.value as any).prices
  if (pricesField?.$each?.$response?.$errors?.[index]?.[field]?.length) {
    return pricesField.$each.$response.$errors[index][field][0].$message
  }
  return null
}

const hasPriceError = (index: number, field: 'label' | 'price'): boolean => {
  return !!getPriceError(index, field)
}

const wefoImageChannels = [
  { channel: 'wefo-1', label: 'Bild 1', dimensions: '432 × 528 px', width: 432, height: 528 },
  { channel: 'wefo-2', label: 'Bild 2', dimensions: '396 × 438 px', width: 396, height: 438 },
  { channel: 'wefo-3', label: 'Bild 3', dimensions: '545 × 438 px', width: 545, height: 438 },
  { channel: 'wefo-4', label: 'Bild 4', dimensions: '1078 × 1080 px', width: 1078, height: 1080 },
]
</script>

<template>
  <div>
    <!-- WeFo Enable Toggle -->
    <div class="form-control mb-4">
      <label class="label cursor-pointer justify-start">
        <input
          type="checkbox"
          class="toggle toggle-primary mr-2"
          v-model="enabled"
          :true-value="true"
          :false-value="false"
        />
        <span class="label-text font-medium">Im Welterbe-Forum anzeigen</span>
      </label>
    </div>

    <!-- WeFo Fields (conditional) -->
    <div v-show="enabled">
      <div class="divider"></div>

      <h6 class="text-md font-semibold mb-3">Kachel & Titel</h6>
      <div class="flex flex-wrap gap-x-4">
        <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
          <div class="label"><span class="label-text">Kachel-Titel</span></div>
          <input type="text" v-model="data.tile_title" @blur="v$.tile_title?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('tile_title') }" maxlength="255" placeholder="z.B. Zollverein Eisbahn" />
          <div v-if="getError('tile_title')" class="label"><span class="label-text-alt text-error">{{ getError('tile_title') }}</span></div>
        </label>
        <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
          <div class="label"><span class="label-text">Titel 1</span></div>
          <input type="text" v-model="data.title1" @blur="v$.title1?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('title1') }" maxlength="255" placeholder="z.B. Eisbahn" />
          <div v-if="getError('title1')" class="label"><span class="label-text-alt text-error">{{ getError('title1') }}</span></div>
        </label>
        <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
          <div class="label"><span class="label-text">Titel 2</span></div>
          <input type="text" v-model="data.title2" @blur="v$.title2?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('title2') }" maxlength="255" placeholder="z.B. auf dem Welterbe" />
          <div v-if="getError('title2')" class="label"><span class="label-text-alt text-error">{{ getError('title2') }}</span></div>
        </label>
      </div>

      <div class="divider"></div>

      <h6 class="text-md font-semibold mb-3">Beschreibung & Ort</h6>
      <label class="form-control w-full mb-2">
        <div class="label"><span class="label-text">Beschreibung</span></div>
        <textarea v-model="data.description" @blur="v$.description?.$touch()" class="textarea textarea-bordered w-full" :class="{ 'textarea-error': hasError('description') }" rows="4" maxlength="5000" placeholder="Beschreibungstext..."></textarea>
        <div v-if="getError('description')" class="label"><span class="label-text-alt text-error">{{ getError('description') }}</span></div>
      </label>
      <div class="flex flex-wrap gap-x-4">
        <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
          <div class="label"><span class="label-text">Ort / Standort-Text</span></div>
          <input type="text" v-model="data.location_text" @blur="v$.location_text?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('location_text') }" maxlength="255" placeholder="z.B. Schacht XII, Halle 5" />
          <div v-if="getError('location_text')" class="label"><span class="label-text-alt text-error">{{ getError('location_text') }}</span></div>
        </label>
        <label class="form-control w-full md:w-[calc(50%-0.5rem)]">
          <div class="label"><span class="label-text">Fußweg (Minuten)</span></div>
          <input type="number" v-model.number="data.walking_time" @blur="v$.walking_time?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasError('walking_time') }" placeholder="z.B. 12" />
          <div v-if="getError('walking_time')" class="label"><span class="label-text-alt text-error">{{ getError('walking_time') }}</span></div>
        </label>
      </div>

      <div class="divider"></div>

      <h6 class="text-md font-semibold mb-3">Index-Titel</h6>
      <div class="form-control mb-2">
        <label class="label cursor-pointer justify-start">
          <input type="checkbox" v-model="data.index_enabled" class="checkbox checkbox-sm checkbox-primary mr-2" />
          <span class="label-text">Index-Titel hinzufügen</span>
        </label>
      </div>
      <div v-if="data.index_enabled">
        <div v-for="(title, index) in data.index_titles" :key="index" class="flex items-end gap-2 mb-2">
          <label class="form-control flex-1">
            <div v-if="index === 0" class="label"><span class="label-text">Index-Titel</span></div>
            <input type="text" v-model="data.index_titles[index]" class="input input-bordered input-sm w-full" maxlength="255" placeholder="Index-Eintrag" />
          </label>
          <button type="button" class="btn btn-ghost btn-sm btn-square text-error" @click="removeIndexTitle(index)" title="Entfernen">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
          </button>
        </div>
        <button type="button" class="btn btn-outline btn-sm mt-1" @click="addIndexTitle">+ Index-Titel hinzufügen</button>
      </div>

      <div class="divider"></div>

      <h6 class="text-md font-semibold mb-3">Preise</h6>
      <div class="form-control mb-2">
        <label class="label cursor-pointer justify-start">
          <input type="checkbox" v-model="data.free_admission" class="checkbox checkbox-sm checkbox-primary mr-2" />
          <span class="label-text">Eintritt frei</span>
        </label>
      </div>
      <div v-if="!data.free_admission">
        <div v-for="(price, index) in data.prices" :key="index" class="flex items-end gap-2 mb-2">
          <label class="form-control flex-1">
            <div v-if="index === 0" class="label"><span class="label-text">Bezeichnung</span></div>
            <input type="text" v-model="price.label" @blur="v$.prices?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasPriceError(index, 'label') }" maxlength="255" placeholder="z.B. Erwachsene" />
            <div v-if="getPriceError(index, 'label')" class="label"><span class="label-text-alt text-error">{{ getPriceError(index, 'label') }}</span></div>
          </label>
          <label class="form-control w-40">
            <div v-if="index === 0" class="label"><span class="label-text">Preis</span></div>
            <input type="text" v-model="price.price" @blur="v$.prices?.$touch()" class="input input-bordered input-sm w-full" :class="{ 'input-error': hasPriceError(index, 'price') }" maxlength="255" placeholder="z.B. 5,00 €" />
            <div v-if="getPriceError(index, 'price')" class="label"><span class="label-text-alt text-error">{{ getPriceError(index, 'price') }}</span></div>
          </label>
          <button type="button" class="btn btn-ghost btn-sm btn-square text-error" @click="removePrice(index)" title="Entfernen">
            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>
          </button>
        </div>
        <button type="button" class="btn btn-outline btn-sm mt-1" @click="addPrice">+ Preis hinzufügen</button>

        <div class="mt-4">
          <div class="label"><span class="label-text">Preis-Level</span></div>
          <div class="flex gap-4">
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="data.price_level" value="" class="radio radio-sm" />
              <span class="label-text">Keins</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="data.price_level" value="€" class="radio radio-sm" />
              <span class="label-text">€</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="data.price_level" value="€€" class="radio radio-sm" />
              <span class="label-text">€€</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" v-model="data.price_level" value="€€€" class="radio radio-sm" />
              <span class="label-text">€€€</span>
            </label>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <h6 class="text-md font-semibold mb-3">Icons für Barrierefreiheit</h6>
      <icon-select
        :all-icons="allIcons || []"
        :selected-icons="selectedIcons || []"
        @update:model-value="onIconsChanged"
      />

      <!-- WeFo Images (only for existing locations) -->
      <template v-if="locationId">
        <div class="divider"></div>
        <h6 class="text-md font-semibold mb-3">Bilder</h6>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="img in wefoImageChannels" :key="img.channel">
            <div class="text-sm font-medium mb-1">{{ img.label }} <span class="text-gray-400">({{ img.dimensions }})</span></div>
            <ShowImage :parent="{ id: locationId, type: 'location', channel: img.channel }" :required-width="img.width" :required-height="img.height" />
          </div>
        </div>
      </template>
      <div v-else class="text-sm text-gray-500 italic mt-4">
        Bilder können nach dem Erstellen der Location hochgeladen werden.
      </div>
    </div>
  </div>
</template>
